import React from "react";
import {
  ThemeProvider,
  StyledEngineProvider,
  useTheme
} from "@mui/material/styles";
import makeStyles from "@mui/styles/makeStyles";
import learningCenterTheme from "../../theme/learningCenterTheme";
import ThemeWrapper from "../../theme/ThemeWrapper";
import { useSelector, useDispatch } from "react-redux";
import useLoadingState from "../../hooks/useLoadingState";
import LoadingSpinner from "../../components/LoadingSpinner/LoadingSpinner";
import {
  AppBar,
  Toolbar,
  Box,
  Button,
  Container,
  Typography
} from "@mui/material";
import { ExitToApp, ArrowBack } from "@mui/icons-material";
import {
  coursePreview as coursePreviewRoute,
  activityPreview as activityPreviewRoute,
  buildActivityPreviewRoute,
  buildCoursePreviewRoute
} from "../../routes/Routes";
import { Route } from "react-router-dom";
import CoursePreview from "./CoursePreview";
import ActivityPreview from "./ActivityPreview";
import { push } from "connected-react-router";
import { useCoursePreview } from "../../hooks/coursePreview";
import ButtonGroup from "../../components/Buttons/ButtonGroup";
import { activityTypes, courseSectionTypes } from "../../constants/index";
import { useTranslation } from "react-i18next";
import { sortBy } from "lodash";

const getResourceSurveyRoute = (courseSections, versionId) => {
  let resourceSurveySectionId = null;
  let resourceSurveyActivityId = null;
  courseSections.forEach(({ sectionType, courseContentables, id }) => {
    if (
      !resourceSurveyActivityId &&
      sectionType === courseSectionTypes.optional
    ) {
      let surveyActivity = courseContentables.find(
        ({ type }) => type === activityTypes.survey
      );
      if (!!surveyActivity) {
        resourceSurveySectionId = id;
        resourceSurveyActivityId = surveyActivity.id;
      }
    }
  });

  return !!resourceSurveyActivityId
    ? buildActivityPreviewRoute(
        versionId,
        resourceSurveySectionId,
        resourceSurveyActivityId
      )
    : null;
};

const getNextCourseSectionMandatory = (courseSections, currentSectionIndex) => {
  const nextSection = courseSections[currentSectionIndex + 1];
  return nextSection;
};

const getNextCourseSectionOptional = (courseSections, currentSectionIndex) => {
  const nextSection = courseSections[currentSectionIndex + 1];
  if (!nextSection) {
    // If the user is on the last course Section in the resources section, take them back to the first section
    // in the table of contents that is not the overview.
    return courseSections.find(({ isOverview }) => !isOverview);
  } else {
    // Otherwise return the next course section
    return nextSection;
  }
};

const useStyles = makeStyles(theme => ({
  appBar: {
    height: 60,
    zIndex: theme.zIndex.drawer + 1,
    background: theme.gradient,
    color: theme.palette.common.white,
    boxShadow: "0px 7px 10px -10px rgba(0,0,0,0.3)"
  }
}));

const Preview = ({ versionId, courseSectionId, activityId }) => {
  const classes = useStyles();
  const theme = useTheme();
  const { t } = useTranslation();
  const { coursePreview, isLoading } = useCoursePreview(versionId);
  let currentSectionIndex = null;
  let activity = null;
  let nextActivity = null;
  let prevActivity = null;
  let nextCourseSection = null;
  let currentSection = null;
  const dispatch = useDispatch();

  if (!!coursePreview && courseSectionId) {
    const courseSections = sortBy(coursePreview.courseSections, "position");
    currentSectionIndex = courseSections.findIndex(
      ({ id }) => id === courseSectionId
    );
    currentSection = courseSections[currentSectionIndex];
    const courseContentables = sortBy(
      currentSection.courseContentables,
      "position"
    );

    const activityIndex = courseContentables.findIndex(
      ({ id }) => `${id}` === activityId
    );
    activity = courseContentables[activityIndex];

    nextActivity = courseContentables[activityIndex + 1];
    prevActivity = courseContentables[activityIndex - 1];
    if (currentSection.sectionType === courseSectionTypes.mandatory) {
      let courseSectionsMandatory = courseSections.filter(
        ({ sectionType }) => sectionType === courseSectionTypes.mandatory
      );
      let currentSectionMandatoryIndex = courseSectionsMandatory.findIndex(
        ({ id }) => id === courseSectionId
      );
      nextCourseSection = getNextCourseSectionMandatory(
        courseSectionsMandatory,
        currentSectionMandatoryIndex
      );
    } else {
      let courseSectionsOptional = courseSections.filter(
        ({ sectionType }) => sectionType === courseSectionTypes.optional
      );
      let currentSectionOptionalIndex = courseSectionsOptional.findIndex(
        ({ id }) => id === courseSectionId
      );
      nextCourseSection = getNextCourseSectionOptional(
        courseSectionsOptional,
        currentSectionOptionalIndex
      );
    }
  }

  const handlePreviewActivityClick = (activityId, courseSectionId) => {
    dispatch(
      push(buildActivityPreviewRoute(versionId, courseSectionId, activityId))
    );
  };

  const handleReturnToCourse = () => {
    dispatch(push(buildCoursePreviewRoute(versionId)));
  };

  const handleNextActivityClick = () => {
    if (nextActivity) {
      handlePreviewActivityClick(nextActivity.id, courseSectionId);
    } else {
      dispatch(push(buildCoursePreviewRoute(versionId)));
    }
  };

  const handlePrevActivityClick = () => {
    handlePreviewActivityClick(prevActivity.id, courseSectionId);
  };

  const handleGoToFirstActivity = () => {
    let nextCourseSection = null;
    const courseSections = sortBy(coursePreview.courseSections, "position");
    const mandatoryCourseSections = courseSections.filter(
      ({ sectionType }) => sectionType === courseSectionTypes.mandatory
    );
    // Direct the user to the first course section in the table of contents
    if (mandatoryCourseSections.length > 1) {
      nextCourseSection = mandatoryCourseSections[1];
    } else {
      nextCourseSection = courseSections[1];
    }
    const firstActivity =
      nextCourseSection &&
      nextCourseSection.courseContentables.find(
        contentable => contentable.position === 1
      );
    if (firstActivity) {
      dispatch(
        push(
          buildActivityPreviewRoute(
            versionId,
            nextCourseSection.id,
            firstActivity.id
          )
        )
      );
    } else {
      dispatch(push(buildCoursePreviewRoute(versionId)));
    }
  };

  const handleGoToNextSection = () => {
    const nextCourseSectionContentables = sortBy(
      nextCourseSection.courseContentables,
      "position"
    );
    const nextCourseSectionFirstActivity = nextCourseSectionContentables[0];
    if (!!nextCourseSectionFirstActivity) {
      dispatch(
        push(
          buildActivityPreviewRoute(
            versionId,
            nextCourseSection.id,
            nextCourseSectionFirstActivity.id
          )
        )
      );
    }
  };

  const handleGoToSurvey = () => {
    dispatch(
      push(getResourceSurveyRoute(coursePreview.courseSections, versionId))
    );
  };

  return (
    <>
      <AppBar position="fixed" className={classes.appBar}>
        <Toolbar>
          <Box
            display="flex"
            flex={1}
            pl={1.5}
            pr={1}
            justifyContent="space-between"
            alignItems="center"
          >
            <Typography
              variant="overline"
              color={theme.palette.common.white}
              sx={{ fontSize: "0.825rem" }}
            >
              {t("coursePreview.coursePreviewHeader")}
            </Typography>
            <ButtonGroup>
              {!!activityId && (
                <Button
                  data-qa="preview-return-to-table-of-contents-btn"
                  startIcon={<ArrowBack />}
                  color="inherit"
                  variant="outlined"
                  onClick={() => handleReturnToCourse()}
                  style={{ textTransform: "none" }}
                >
                  {t("coursePreview.returnToTableOfContentsBtn")}
                </Button>
              )}
              <Button
                data-qa="preview-exit-btn"
                endIcon={<ExitToApp />}
                color="inherit"
                variant="outlined"
                onClick={() => window.close()}
              >
                {t("coursePreview.exitPreviewBtn")}
              </Button>
            </ButtonGroup>
          </Box>
        </Toolbar>
      </AppBar>
      <Box mt={`${theme.sizes.topNavBarHeight}px`} mb={13}>
        {isLoading && <LoadingSpinner />}
        {coursePreview && (
          <Container maxWidth="lg" data-qa="course-preview-container">
            <Route
              path={coursePreviewRoute}
              exact
              render={() => (
                <CoursePreview
                  courseTitle={coursePreview.title}
                  courseSections={coursePreview.courseSections}
                  onPreviewActivityClick={handlePreviewActivityClick}
                />
              )}
            />
            <Route
              path={activityPreviewRoute}
              exact
              render={() =>
                coursePreview ? (
                  <ActivityPreview
                    courseId={versionId}
                    activity={activity}
                    isFirstActivity={!prevActivity}
                    isLastActivity={!nextActivity}
                    isLastSection={!nextCourseSection}
                    nextCourseSectionTitle={nextCourseSection?.title}
                    courseData={coursePreview}
                    courseSectionTitle={currentSection.title}
                    onViewOutlineClick={handleReturnToCourse}
                    onNextActivityClick={handleNextActivityClick}
                    onPrevActivityClick={handlePrevActivityClick}
                    onGetStartedClick={handleGoToFirstActivity}
                    hasSurvey={
                      !!getResourceSurveyRoute(
                        coursePreview.courseSections,
                        versionId
                      )
                    }
                    onGoToNextSectionClick={handleGoToNextSection}
                    onGoToSurveyClick={handleGoToSurvey}
                  />
                ) : null
              }
            />
          </Container>
        )}
      </Box>
    </>
  );
};

const PreviewContainer = ({
  match: {
    params: { versionId, courseSectionId, activityId }
  }
}) => {
  const theme = useTheme();
  const { customColor, secondaryColor } = useSelector(
    ({ learningCenterSettingsState: { customColor, secondaryColor } }) => ({
      customColor,
      secondaryColor
    })
  );
  const loading = useLoadingState("FETCH_SETTINGS");
  return loading ? (
    <LoadingSpinner />
  ) : (
    <StyledEngineProvider injectFirst>
      <ThemeProvider
        theme={learningCenterTheme(theme, customColor, secondaryColor)}
      >
        <ThemeWrapper>
          <Preview
            versionId={versionId}
            activityId={activityId}
            courseSectionId={courseSectionId}
          />
        </ThemeWrapper>
      </ThemeProvider>
    </StyledEngineProvider>
  );
};
export default PreviewContainer;
